import type { Dictionary } from './dictionary';

/**
 * İngilizce sözlük.
 *
 * `Dictionary` tipi Türkçe sözlükten türetilir; bir anahtar eksikse veya
 * fazlaysa bu dosya DERLENMEZ. Çeviri unutmak bir çalışma zamanı sürprizi
 * değil, derleme hatasıdır.
 */
export const en: Dictionary = {
  common: {
    save: 'Save',
    cancel: 'Cancel',
    close: 'Close',
    confirm: 'Confirm',
    ok: 'OK',
    loading: 'Loading…',
    retry: 'Try again',
    currencySuffix: '₺',
  },

  a11y: {
    mainNavigation: 'Main navigation',
    sections: 'Sections',
    logout: 'Log out',
    checkingSession: 'Checking your session',
    languageSwitcher: 'Language',
    themeSwitcher: 'Appearance',
    homeLink: 'Zee Sports Studio home page',
  },

  theme: {
    system: 'System',
    light: 'Light',
    dark: 'Dark',
  },

  roles: {
    ADMIN: 'Owner',
    INSTRUCTOR: 'Instructor',
    CLIENT: 'Member',
  },

  nav: {
    CALENDAR: 'Class Schedule',
    PACKAGES: 'My Packages',
    TRAINER: 'Enrolments & Packages',
    ADMIN: 'Admin & Reports',
    PROFILE: 'My Profile',
  },

  navShort: {
    CALENDAR: 'Schedule',
    PACKAGES: 'Packages',
    TRAINER: 'Enrolments',
    ADMIN: 'Admin',
    PROFILE: 'Profile',
  },

  shortcuts: {
    title: 'Keyboard shortcuts',
    search: 'Focus the schedule search',
    goHint: (key: string) => `g then ${key}`,
    help: 'Open this list',
    close: 'Close the dialog',
  },

  auth: {
    tagline: 'Reformer, mat and functional training studio. Sign in to see your programme.',
    methodTablist: 'Sign-in method',
    login: 'Sign In',
    register: 'Sign Up',
    createAccount: 'Create Account',
    email: 'Email',
    emailPlaceholder: 'you@example.com',
    identifier: 'Email or phone',
    identifierPlaceholder: 'you@example.com or 05XX XXX XX XX',
    password: 'Password',
    passwordHint:
      'At least 8 characters, including uppercase, lowercase, a digit and a special character.',
    fullName: 'Full Name',
    fullNamePlaceholder: 'Selin Yılmaz',
    phone: 'Phone',
    phonePlaceholder: '0532 555 12 34',
    cookieNotice: 'Your session is protected by an httpOnly cookie',
    forgotLink: 'Forgot password',
    forgotTitle: 'Forgot your password?',
    forgotBody: "Enter your email; if the account exists, we'll send a reset link.",
    forgotSubmit: 'Send reset link',
    forgotSent:
      "If your email is registered, we've sent a password reset link. Check your inbox — and your spam folder. The link is valid for 30 minutes.",
    backToLogin: 'Back to sign in',
    resetTitle: 'Set a new password',
    resetBody: 'Choose a new password only you know for your account.',
    newPasswordLabel: 'New Password',
    resetSubmit: 'Update password',
    resetInvalid: 'This reset link is invalid or has expired.',
    resetRequestNew: 'Request a new link',
  },

  passwordRotation: {
    title: 'Set Your Password',
    description:
      'Your account was created by studio staff with a temporary password. Before continuing, choose a password only you know.',
    currentPassword: 'Temporary Password',
    currentPasswordHint: 'The one-time password you were given.',
    newPassword: 'New Password',
    submit: 'Update Password',
    success: 'Password updated.',
    signOut: 'Cancel and sign out',
  },

  account: {
    title: 'Account Settings',
    open: 'Go to my profile',
    emailLabel: 'Email',
    emailHint: 'Your email is your sign-in identity; it cannot be changed here.',
    phoneLabel: 'Phone',
    changePasswordTitle: 'Change Password',
    changePasswordDescription:
      'When your password changes, sessions on other devices are signed out for safety.',
    currentPassword: 'Current Password',
    newPassword: 'New Password',
    submit: 'Update Password',
    appearanceTitle: 'Appearance & Language',
    themeLabel: 'Theme',
    languageLabel: 'Language',
  },

  schedule: {
    title: 'Class Schedule',
    memberSubtitle: 'Your classes, scheduled from your packages.',
    staffSubtitle: 'Every class in the studio — package, members, instructor and room.',
    previousWeek: 'Previous week',
    nextWeek: 'Next week',
    today: 'Today',
    viewModeLabel: 'View',
    viewWeek: 'Week',
    viewList: 'List',
    daysLabel: 'Days of the week',
    loading: 'Loading the schedule',
    loadError: 'The schedule could not be loaded.',
    emptyDay: 'No classes',
    dayCount: (count: number) => `${count} ${count === 1 ? 'class' : 'classes'}`,
    emptyMemberTitle: 'No classes this week',
    emptyMemberBody:
      'Your classes appear here as your instructor schedules your package. Use the arrows to browse other weeks.',
    lessonOf: (current: number, total: number) => `Class ${current}/${total}`,
    seats: (count: number, capacity: number) => `${count}/${capacity} people`,
    more: (count: number) => `+${count}`,
    cancelled: 'Cancelled',
    rescheduled: 'Rescheduled',
    makeupLesson: 'Make-up',
    trainer: 'Instructor',
    room: 'Room',
    time: 'Time',
    packageLabel: 'Package',
    detailTitle: 'Class details',
    attendance: {
      PRESENT: 'Attended',
      LATE: 'Late',
      ABSENT: 'Absent',
      EXCUSED: 'Excused',
    },
    attendancePending: 'Attendance not taken yet',
    makeupUsed: 'Absence allowance used',
    makeupNotice: (hours: number) =>
      `If you cannot attend, report it here at least ${hours} hours before the class; your instructor is notified.`,
    makeupClosed: (hours: number) =>
      `Less than ${hours} hours to the class; if you cannot attend, tell your instructor.`,
    makeupRequest: 'Report absence',
    makeupConfirm: 'You are reporting that you cannot attend this class; one excused absence is used.',
    makeupConfirmButton: 'Yes, report',
    makeupRights: (used: number, allowed: number) => `Excused absences: ${used}/${allowed}`,
    makeupExhausted: 'You have used all excused absences; tell your instructor if you cannot attend.',
    makeupDone: 'Absence recorded; your instructor has been notified.',
  },

  myPackages: {
    title: 'My Packages',
    subtitle: 'Your packages are set up at the studio; follow your progress and absence allowance here.',
    loading: 'Loading your packages',
    loadError: 'Your packages could not be loaded.',
    emptyTitle: 'No packages yet',
    emptyBody:
      'Packages are purchased at the studio and assigned to your account by your instructor. Once assigned, your classes appear here and in the Class Schedule.',
    status: {
      ACTIVE: 'In progress',
      COMPLETED: 'Completed',
      CANCELLED: 'Cancelled',
    },
    groupBadge: 'Group class',
    progressLabel: 'Package progress',
    done: (count: number, total: number) => `${count}/${total} classes done`,
    upcoming: (count: number) => `${count} scheduled`,
    unscheduled: (count: number) => `${count} to be scheduled`,
    absences: (used: number, allowed: number) => `Absence allowance ${used}/${allowed}`,
    noAbsences: 'No absence allowance in this package',
    plan: 'Plan',
    trainer: 'Instructor',
    room: 'Room',
    started: 'Start',
    validUntil: 'Valid until',
    nextLesson: 'Next class',
    noNextLesson: 'No class scheduled',
  },

  profile: {
    title: 'My Profile',
    subtitle: 'Your account, display preferences and membership details.',
    accountTitle: 'Account',
    healthTitle: 'Posture & Health Notes',
    healthDescription: 'Your instructor plans each session around these notes.',
    goalLabel: 'Your goal',
    goalEmpty: 'No goal set yet.',
    goalPlaceholder: 'e.g. Core strength & posture correction',
    notesLabel: 'Injury / Posture Notes',
    notesEmpty: 'You have not added any injury or posture notes for your instructor yet.',
    notesPlaceholder: 'e.g. Mild L5-S1 lower back sensitivity, right shoulder tension',
    notesHint: 'This is health data visible only to you and your instructors.',
    healthEdit: 'Edit notes',
    healthAdd: 'Add health notes',
    agreementTitle: 'Membership Agreement',
    agreementSigned: (name: string, date: string, version: string) =>
      `Accepted by ${name} on ${date} · version ${version}`,
    agreementOutdated: 'The agreement has been updated; you will be asked to accept the new text next time you sign in.',
    agreementNone: 'No acceptance on record.',
    agreementRead: 'Read the agreement',
    previewNotice: 'You are previewing: account settings and health notes appear on the member’s own screen.',
  },

  agreement: {
    title: 'Membership Agreement',
    intro:
      'Before your classes begin, please read and accept the studio’s membership agreement. Your acceptance is recorded with your name and the date.',
    readHint: 'Please read the text to the end.',
    readDone: 'You have reached the end of the text.',
    nameLabel: 'Full Name',
    nameHint: 'For members under 18, the parent’s or guardian’s full name.',
    nameError: 'Enter your full name (at least 3 characters).',
    dateLabel: 'Date',
    accept: 'I have read, understood and accept the agreement.',
    submit: 'Accept and continue',
    signOut: 'Sign out',
    version: (version: string) => `Agreement version: ${version}`,
    turkishOnly:
      'The agreement is published in Turkish; the Turkish text is the one you accept.',
  },

  viewAs: {
    banner: (name: string, role: string) => `Viewing as ${name} (${role})`,
    exit: 'Back to my view',
  },

  instructorModal: {
    dayNames: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'],
    experience: (years: number) => `${years} years of experience`,
    pt: 'Private PT',
    group: 'Group Classes',
    certifications: 'Certifications',
    weeklyProgram: 'Weekly Schedule',
    reviews: 'Member Reviews',
  },

  errorFallback: {
    title: 'Something went wrong',
    body: 'An unexpected error occurred. Reloading the page usually fixes it.',
    supportCode: (id: string) => `Support code: ${id}`,
    retry: 'Try Again',
    reload: 'Reload Page',
    panelTitle: 'This section could not be loaded',
    unknown: 'Unknown error',
  },

  errors: {
    network: 'Cannot reach the server. Check your connection and try again.',
    unexpected: 'Something went wrong.',
    unexpectedResponse: 'The server returned an unexpected response.',
  },

  footer: {
    studioName: 'Zee Sports Studio',
    copyright: '© 2026',
    tags: ['Reformer', 'One-on-one', 'Functional'],
  },
};
